/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { StorageModule } from "@vlinder-web/storage-module-react";
import toast from "react-hot-toast";
import { CREATE_MULTI_BOX } from "../graphql/createMultiBox.mutation";
import { MULTI_REG_BOX } from "../graphql/multiregisterBox.mutation";
import { REGISTER_FISERV_PAYMENT } from "../graphql/registerFiservPayment.mutation";
import { handleFormPaymentResponse } from "../../../utils/formPayment.util";

const log: any = LoggerModule?.getInstance();
const storageService = StorageModule.getInstance();

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ICreateMultiBoxMethods extends IPluginMethodMap {
  CreateMultiBox(args: any): Promise<any>;
}

export class CreateMultiBoxService implements IAgentPlugin {
  readonly methods: ICreateMultiBoxMethods;

  constructor() {
    this.methods = {
      CreateMultiBox: this.CreateMultiBox.bind(this),
    };
  }

  async CreateMultiBox(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      console.log(
        "args12345create",
        args,
        this.transformData(args?.createMulti)
      );
      if (client) {
        let variables: any = {
          data: this.transformData(args?.createMulti),
        };
        const res = await client.mutate({
          mutation: CREATE_MULTI_BOX,
          variables: variables,
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          const provider = storageService.get(
            "currencyExchangeLocal",
            "local"
          )?.provider;
          if (provider === "FISERV") {
            const conversionRate =
              storageService.get("currencyExchangeLocal", "local")
                ?.conversion_rate || 1;
            const fiservVariables = this.transformFiservResData(
              args?.event,
              args?.items,
              res,
              conversionRate
            );
            console.log("fiservVariables", fiservVariables);

            const resFiserv = await client.mutate({
              mutation: REGISTER_FISERV_PAYMENT,
              variables: fiservVariables,
              fetchPolicy: "no-cache",
            });

            const fiservPayRes = resFiserv?.data?.registerFiservPayment;
            if (fiservPayRes) {
              handleFormPaymentResponse(fiservPayRes);
              return {
                submitted: true,
                ...fiservPayRes,
              };
            }
          }

          let variables1 = this.transformResData(args?.event, args?.items, res);
          console.log("args12345create", variables1);
          const res1 = await client.mutate({
            mutation: MULTI_REG_BOX,
            variables: variables1,
            fetchPolicy: "no-cache",
          });
          if (res1?.data) {
            const paymentUrl =
              res1?.data?.multiRegisterPayment?.data?.session[
                "payment-hosted-url"
              ];
            if (paymentUrl) {
              window.location.href = paymentUrl;
              console.log(`Redirecting to: ${paymentUrl}`);
              return {
                ...res1?.data?.multiRegisterPayment,
                redirection: true,
              };
            }
            toast.success("Costumes Created !");
            return {
              ...res1?.data?.multiRegisterPayment,
              redirection: false,
            };
          }
          toast.error("Box created but payment failed!");
          return true;
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find(
          (item: any) => item?.extensions?.display_message
        )?.extensions?.display_message;

      const errorMessage =
        _display_message ||
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.message ||
        err?.message ||
        "Some unexpected error occurred";

      throw Error(errorMessage);
    }
  }

  private buildAllocations(
    itemsIndex: any,
    rawPrice: number,
    entry?: any,
    event?: any
  ) {
    const boxItemOptions =
      Array.isArray(entry?.items) && entry.items.length > 0
        ? entry.items
        : Array.isArray(entry?.boxItems) && entry.boxItems.length > 0
        ? entry.boxItems
        : Array.isArray(itemsIndex?.boxItems) && itemsIndex.boxItems.length > 0
        ? itemsIndex.boxItems
        : Array.isArray(itemsIndex?.items)
        ? itemsIndex.items
        : Array.isArray(itemsIndex?.items?.items)
        ? itemsIndex.items.items
        : [];

    const entryItems = Array.isArray(entry?.items)
      ? entry.items
      : Array.isArray(entry?.boxItems)
      ? entry.boxItems
      : [];

    const mapped = boxItemOptions
      .map((itm: any, idx: number) => {
        const costumeValueId =
          itm?.costumeValue?.id ??
          (typeof itm?.costumeValue === "string"
            ? itm?.costumeValue
            : undefined) ??
          itm?.costumeValueId ??
          itm?.costumeValue ??
          "";

        const entryMatch =
          entryItems.find((ei: any) => {
            const eiValId =
              ei?.costumeValue?.id ??
              (typeof ei?.costumeValue === "string"
                ? ei?.costumeValue
                : undefined) ??
              ei?.costumeValueId ??
              ei?.costumeValue ??
              "";
            return eiValId && eiValId === costumeValueId;
          }) ?? entryItems[idx];

        const price =
          parseFloat(
            itm?.cost ??
              itm?.price ??
              itm?.totalAmt ??
              itm?.bookedPrice ??
              itm?.costumeValue?.topupPrice ??
              itm?.valuePrice ??
              itm?.featurePrice ??
              entryMatch?.cost ??
              entryMatch?.price ??
              entryMatch?.totalAmt ??
              entryMatch?.bookedPrice ??
              entryMatch?.costumeValue?.topupPrice ??
              0
          ) || 0;

        const paidAmount =
          parseFloat(
            itm?.paidAmount ??
              itm?.alreadyPaid ??
              itm?.itemPaidAmount ??
              itm?.amountPaid ??
              entryMatch?.paidAmount ??
              entryMatch?.alreadyPaid ??
              entryMatch?.itemPaidAmount ??
              entryMatch?.amountPaid ??
              0
          ) || 0;

        const isPaid =
          Boolean(itm?.isPaid ?? entryMatch?.isPaid) ||
          (price > 0 && paidAmount >= price);

        const remainingCost = isPaid
          ? 0
          : Math.max(0, Number((price - paidAmount).toFixed(2)));

        const minDp =
          parseFloat(
            itm?.minDownPayment ??
              itm?.costumeValue?.minDownPayment ??
              entryMatch?.minDownPayment ??
              entryMatch?.costumeValue?.minDownPayment ??
              0
          ) || 0;

        const remainingMinDp = isPaid
          ? 0
          : Math.max(
              0,
              Math.min(remainingCost, Number((minDp - paidAmount).toFixed(2)))
            );

        const amount =
          typeof itm?.amount === "number"
            ? itm.amount
            : itm?.amount === 0 || itm?.amount === "0"
            ? 0
            : parseFloat(itm?.amount ?? "") || undefined;

        return {
          costumeValueId,
          price,
          paidAmount,
          isPaid,
          remainingCost,
          minDp,
          remainingMinDp,
          amount,
        };
      })
      .filter((item: any) => Boolean(item.costumeValueId));

    if (!rawPrice || rawPrice <= 0) {
      rawPrice =
        Number(itemsIndex?.enteredAmount || itemsIndex?.amount || 0) ||
        Number(event?.totalAmountPaid || 0) ||
        (event?.currency === "USD"
          ? Number((Number(event?.amount || 0) * 6.25).toFixed(2))
          : Number(event?.amount || 0)) ||
        Number(entry?.totalAmount || 0);
    }

    const passedAllocations =
      Array.isArray(itemsIndex?.allocations) &&
      itemsIndex.allocations.length > 0
        ? itemsIndex.allocations
        : Array.isArray(event?.allocations) && event.allocations.length > 0
        ? event.allocations
        : null;

    if (passedAllocations) {
      const filtered = passedAllocations
        .map((alloc: any) => {
          const valId = alloc?.costumeValueId || alloc?.costumeValue || "";
          const matchedItem = mapped.find(
            (m: any) => m.costumeValueId === valId
          );
          if (matchedItem && matchedItem.isPaid) {
            return null;
          }
          const allocAmt = Number(alloc?.amount ?? rawPrice);
          const maxAllowed =
            matchedItem && matchedItem.remainingCost > 0
              ? matchedItem.remainingCost
              : allocAmt;
          return {
            costumeValueId: valId,
            amount: Number(Math.min(allocAmt, maxAllowed).toFixed(2)),
          };
        })
        .filter((alloc: any) => alloc && (alloc?.amount ?? 0) > 0);
      if (filtered.length > 0) {
        return filtered;
      }
    }

    if (mapped.length === 0) {
      const fallbackId =
        itemsIndex?.costumeValueId ?? itemsIndex?.costumeValue ?? "";
      if (fallbackId && rawPrice > 0) {
        return [{ costumeValueId: fallbackId, amount: rawPrice }];
      }
      return [];
    }

    // Only allocate to items where isPaid === false and remainingCost > 0 (or price not set yet)
    let unpaidItems = mapped.filter(
      (m: any) => !m.isPaid && (m.remainingCost > 0 || m.price === 0)
    );

    if (unpaidItems.length === 0) {
      const notPaidItems = mapped.filter(
        (m: any) => !m.isPaid && m.paidAmount === 0
      );
      if (notPaidItems.length > 0 && rawPrice > 0) {
        unpaidItems = notPaidItems.map((m: any) => ({
          ...m,
          remainingCost: rawPrice,
          remainingMinDp: m.minDp,
        }));
      } else if (mapped.length > 0) {
        unpaidItems = mapped;
      } else {
        return [];
      }
    }

    const hasExplicitAmounts = unpaidItems.some(
      (m: any) => m.amount !== undefined && m.amount > 0
    );

    if (hasExplicitAmounts) {
      return unpaidItems
        .map((m: any) => ({
          costumeValueId: m.costumeValueId,
          amount: Number(
            Math.min(
              m.amount ?? 0,
              m.remainingCost > 0 ? m.remainingCost : m.amount ?? 0
            ).toFixed(2)
          ),
        }))
        .filter((alloc: any) => (alloc?.amount ?? 0) > 0);
    }

    const totalRemainingMinDp = unpaidItems.reduce(
      (sum: number, m: any) => sum + m.remainingMinDp,
      0
    );
    const totalRemainingCost = unpaidItems.reduce(
      (sum: number, m: any) => sum + m.remainingCost,
      0
    );
    const effectivePrice = Math.min(
      rawPrice,
      totalRemainingCost > 0 ? totalRemainingCost : rawPrice
    );

    if (effectivePrice <= 0) {
      return [];
    }

    let remainingToDistribute = effectivePrice;

    if (totalRemainingMinDp > 0) {
      const dpToDistribute = Math.min(
        remainingToDistribute,
        totalRemainingMinDp
      );
      let dpAllocatedSoFar = 0;
      const dpEligible = unpaidItems.filter((m: any) => m.remainingMinDp > 0);

      dpEligible.forEach((m: any, idx: number) => {
        const isLast = idx === dpEligible.length - 1;
        let portion = isLast
          ? Number((dpToDistribute - dpAllocatedSoFar).toFixed(2))
          : Number(
              (
                (m.remainingMinDp / totalRemainingMinDp) *
                dpToDistribute
              ).toFixed(2)
            );
        portion = Math.min(portion, m.remainingCost);
        dpAllocatedSoFar += portion;
        m.currentAlloc = portion;
      });
      remainingToDistribute = Number(
        (remainingToDistribute - dpAllocatedSoFar).toFixed(2)
      );
    } else {
      unpaidItems.forEach((m: any) => {
        m.currentAlloc = 0;
      });
    }

    if (remainingToDistribute > 0) {
      const remainingCapacities = unpaidItems.map((m: any) =>
        Math.max(
          0,
          Number((m.remainingCost - (m.currentAlloc || 0)).toFixed(2))
        )
      );
      const totalCapacity = remainingCapacities.reduce(
        (sum: number, cap: number) => sum + cap,
        0
      );

      if (totalCapacity > 0) {
        let leftoverAllocatedSoFar = 0;
        const capacityEligible = unpaidItems.filter(
          (_: any, idx: number) => remainingCapacities[idx] > 0
        );

        capacityEligible.forEach((m: any, idx: number) => {
          const cap = Math.max(
            0,
            Number((m.remainingCost - (m.currentAlloc || 0)).toFixed(2))
          );
          const isLast = idx === capacityEligible.length - 1;
          let portion = isLast
            ? Number(
                (remainingToDistribute - leftoverAllocatedSoFar).toFixed(2)
              )
            : Number(
                ((cap / totalCapacity) * remainingToDistribute).toFixed(2)
              );
          portion = Math.min(portion, cap);
          leftoverAllocatedSoFar += portion;
          m.currentAlloc = Number(((m.currentAlloc || 0) + portion).toFixed(2));
        });
      }
    }

    const finalAllocations = unpaidItems
      .map((m: any) => ({
        costumeValueId: m.costumeValueId,
        amount: Number((m.currentAlloc || 0).toFixed(2)),
      }))
      .filter((alloc: any) => alloc.amount > 0);

    if (finalAllocations.length === 0 && rawPrice > 0 && mapped.length > 0) {
      const targetItem = unpaidItems[0] || mapped[0];
      return [{ costumeValueId: targetItem.costumeValueId, amount: rawPrice }];
    }

    return finalAllocations;
  }

  private transformFiservResData(
    event: any,
    items: any[],
    res1: any,
    conversionRate: number
  ) {
    const createdBoxes = res1?.data?.createMultiBox ?? [];
    const gatewayCurrency =
      storageService.get("currencyExchangeLocal", "local")?.currency || "TTD";
    const userCurrency = event?.currency || "TTD";

    let totalAmount = 0;
    const splitMap = createdBoxes.map((entry: any, index: number) => {
      const costumeId =
        entry?.costumeID ??
        entry?.costumeId ??
        entry?.payOrder?.box?.[0]?.costumeID ??
        entry?.payOrder?.box?.[0]?.costumeId ??
        items?.[index]?.costumeId ??
        "";
      const payOrder =
        typeof entry?.payOrder === "string"
          ? entry.payOrder
          : entry?.payOrder?.id ?? entry?._id;

      let rawPrice =
        items?.[index]?.enteredAmount ?? items?.[index]?.amount ?? 0;
      if (rawPrice <= 0) {
        if (createdBoxes.length === 1) {
          rawPrice =
            Number(event?.totalAmountPaid || 0) ||
            (userCurrency === "USD"
              ? Number((Number(event?.amount || 0) * conversionRate).toFixed(2))
              : Number(event?.amount || 0)) ||
            Number(entry?.totalAmount || 0);
        } else {
          rawPrice = Number(entry?.totalAmount || 0);
        }
      }

      let boxAmount = Number(rawPrice.toFixed(2));
      if (userCurrency === "USD") {
        boxAmount = Number((rawPrice / conversionRate).toFixed(2));
      }
      if (
        boxAmount <= 0 &&
        Number(event?.amount || 0) > 0 &&
        createdBoxes.length === 1
      ) {
        boxAmount = Number(event.amount);
      }
      totalAmount += boxAmount;

      let allocations = this.buildAllocations(
        items?.[index],
        rawPrice,
        entry,
        event
      ).filter((alloc: any) => Number(alloc?.amount || 0) > 0);

      if (allocations.length === 0) {
        const fallbackCostumeValueId =
          entry?.items?.find(
            (it: any) => it?.costumeValue?.id || it?.costumeValue
          )?.costumeValue?.id ??
          entry?.items?.find(
            (it: any) => it?.costumeValue?.id || it?.costumeValue
          )?.costumeValue ??
          items?.[index]?.allocations?.[0]?.costumeValueId ??
          items?.[index]?.boxItems?.find(
            (it: any) => it?.costumeValue?.id || it?.costumeValue
          )?.costumeValue?.id ??
          items?.[index]?.boxItems?.find(
            (it: any) => it?.costumeValue?.id || it?.costumeValue
          )?.costumeValue ??
          items?.[index]?.items?.find(
            (it: any) => it?.costumeValue?.id || it?.costumeValue
          )?.costumeValue?.id ??
          items?.[index]?.items?.find(
            (it: any) => it?.costumeValue?.id || it?.costumeValue
          )?.costumeValue ??
          items?.[index]?.costumeValueId ??
          "";
        if (fallbackCostumeValueId) {
          allocations = [
            {
              costumeValueId: fallbackCostumeValueId,
              amount:
                rawPrice ||
                (boxAmount > 0
                  ? userCurrency === "USD"
                    ? Number((boxAmount * conversionRate).toFixed(2))
                    : boxAmount
                  : 1),
            },
          ];
        }
      }

      return {
        payOrder,
        costumeId,
        amount: boxAmount,
        allocations,
      };
    });

    let calculatedTotalAmount = Number(totalAmount.toFixed(2));
    if (calculatedTotalAmount <= 0 && Number(event?.amount || 0) > 0) {
      calculatedTotalAmount = Number(event.amount);
    }
    let finalPlatformFee = event?.platformFee;
    if (userCurrency === "USD" && finalPlatformFee !== undefined) {
      if (
        finalPlatformFee > 10 &&
        finalPlatformFee > calculatedTotalAmount * 0.1
      ) {
        finalPlatformFee = Number(
          (Number(finalPlatformFee) / conversionRate).toFixed(2)
        );
      }
    }

    return {
      data: {
        amount: calculatedTotalAmount,
        //fix: support ttd
        currency: userCurrency,
        // currency: "USD",
        enable3DS: true,
        groupPayment: true,
        splitMap,
        ...(finalPlatformFee !== undefined && {
          platformFee: finalPlatformFee,
        }),
      },
    };
  }
  private transformResData(event: any, items: any[], res1: any) {
    const createdBoxes = res1?.data?.createMultiBox ?? [];
    const groupId = createdBoxes?.[0]?.groupId;

    const allAllocations = event?.allocations ?? [];

    const splitMap = createdBoxes.map((entry: any, index: number) => {
      const sourceItem = items?.[index];

      const boxItems = entry?.items ?? entry?.payOrder?.box?.[0]?.items ?? [];
      const boxCostumeValueIds =
        boxItems
          .map((item: any) => {
            return (
              item?.costumeValue?.id ?? item?.costumeValue ?? item?.value?.id
            );
          })
          .filter(Boolean) ?? [];

      let boxAllocations = allAllocations.filter((allocation: any) =>
        boxCostumeValueIds.includes(allocation?.costumeValueId)
      );

      // The response may not return box item details.
      // For a single Open Registration costume, use all allocations.
      if (createdBoxes.length === 1 && boxAllocations.length === 0) {
        boxAllocations = allAllocations;
      }

      if (boxAllocations.length === 0) {
        const boxItemOptions = Array.isArray(sourceItem?.items)
          ? sourceItem.items
          : Array.isArray(sourceItem?.items?.items)
          ? sourceItem.items.items
          : [];
        const hasExplicitAmounts = boxItemOptions.some(
          (itm: any) =>
            typeof itm?.amount === "number" ||
            (itm?.amount !== undefined &&
              itm?.amount !== null &&
              itm?.amount !== "")
        );

        if (hasExplicitAmounts) {
          boxAllocations = boxItemOptions
            .map((itm: any) => {
              const cValId =
                itm?.costumeValue?.id ??
                (typeof itm?.costumeValue === "string"
                  ? itm?.costumeValue
                  : undefined) ??
                itm?.costumeValueId ??
                itm?.costumeValue ??
                "";
              const itemAmt =
                typeof itm?.amount === "number"
                  ? itm.amount
                  : parseFloat(itm?.amount ?? 0) || 0;
              return { costumeValueId: cValId, amount: itemAmt };
            })
            .filter(
              (alloc: any) =>
                Boolean(alloc.costumeValueId) && Number(alloc?.amount || 0) > 0
            );
        } else {
          boxAllocations = boxItemOptions
            .map((itm: any) => {
              const cValId =
                itm?.costumeValue?.id ??
                (typeof itm?.costumeValue === "string"
                  ? itm?.costumeValue
                  : undefined) ??
                itm?.costumeValueId ??
                itm?.costumeValue ??
                "";
              const itemAmt =
                typeof itm?.amount === "number"
                  ? itm.amount
                  : parseFloat(itm?.amount ?? 0) ||
                    (typeof itm?.price === "number"
                      ? itm.price
                      : parseFloat(itm?.price ?? 0));
              return { costumeValueId: cValId, amount: itemAmt };
            })
            .filter(
              (alloc: any) => Boolean(alloc.costumeValueId) && alloc.amount > 0
            );
        }
      }

      const allocationAmount = boxAllocations.reduce(
        (total: number, allocation: any) =>
          total + Number(allocation?.amount ?? 0),
        0
      );

      const costumeId =
        entry?.costumeID ?? entry?.payOrder?.box?.[0]?.costumeID;
      const payOrderId =
        typeof entry?.payOrder === "string"
          ? entry.payOrder
          : entry?.payOrder?.id ?? entry?._id;

      const mappedItem: any = {
        allocations: boxAllocations,
        amount:
          allocationAmount > 0
            ? allocationAmount
            : Number(sourceItem?.enteredAmount ?? sourceItem?.amount ?? 0),
        costumeId,
        payOrder: payOrderId,
      };

      return mappedItem;
    });

    const firstPayOrderId =
      typeof createdBoxes?.[0]?.payOrder === "string"
        ? createdBoxes[0].payOrder
        : createdBoxes?.[0]?.payOrder?.id ?? createdBoxes?.[0]?._id;
    const topDiscountCode =
      event?.discountCode ??
      event?.code ??
      items?.[0]?.selectedDiscount ??
      items?.[0]?.discountCode ??
      null;

    let finalPlatformFee = event?.platformFee;
    if (event?.currency === "USD" && finalPlatformFee !== undefined) {
      const conversionRate =
        storageService.get("currencyExchangeLocal", "local")?.conversion_rate ||
        1;
      if (
        finalPlatformFee > 10 &&
        finalPlatformFee > Number(event?.amount || 0) * 0.1
      ) {
        finalPlatformFee = Number(
          (Number(finalPlatformFee) / conversionRate).toFixed(2)
        );
      }
    }

    return {
      data: {
        amount: Number(event?.amount ?? 0),
        currency: event?.currency ?? "TTD",
        groupId,
        groupPayment: true,
        payorder: firstPayOrderId,
        splitMap,
        ...(topDiscountCode && { discountCode: topDiscountCode }),
        ...(finalPlatformFee !== undefined && {
          platformFee: finalPlatformFee,
        }),
        allocations: allAllocations,
      },
    };
  }
  private transformData(inputData?: any) {
    return (inputData || []).map((entry?: any) => {
      const rawItems = Array.isArray(entry?.items)
        ? entry.items
        : entry?.items?.items || [];

      const discountCode =
        entry?.discountCode ||
        entry?.selectedDiscount ||
        entry?.discount ||
        entry?.items?.discountCode ||
        entry?.items?.selectedDiscount ||
        null;

      const mappedItems = rawItems.map((item: any) => {
        const costumeType = item?.costumeType?.id ?? item?.costumeType;
        const costumeFeature = item?.costumeFeature?.id ?? item?.costumeFeature;
        const costumeAttribute =
          item?.costumeAttribute?.id ?? item?.costumeAttribute;
        const costumeValue = item?.costumeValue?.id ?? item?.costumeValue;

        return {
          costumeType,
          costumeFeature,
          costumeAttribute,
          costumeValue,
        };
      });

      const presentation =
        entry?.presentation?.id ??
        (typeof entry?.presentation === "string"
          ? entry.presentation
          : undefined) ??
        entry?.presentationID ??
        entry?.presentationId ??
        (!Array.isArray(entry?.items)
          ? entry?.items?.presentation?.id ??
            (typeof entry?.items?.presentation === "string"
              ? entry?.items?.presentation
              : undefined)
          : undefined) ??
        entry?.content?.presentation?.id ??
        (typeof entry?.content?.presentation === "string"
          ? entry.content?.presentation
          : undefined) ??
        entry?.content?.presentationID ??
        entry?.content?.presentationId ??
        entry?.payOrder?.box?.[0]?.presentation?.id ??
        (typeof entry?.payOrder?.box?.[0]?.presentation === "string"
          ? entry.payOrder?.box?.[0]?.presentation
          : undefined);

      const section =
        entry?.section?.id ??
        (typeof entry?.section === "string" ? entry.section : undefined) ??
        entry?.sectionID ??
        entry?.sectionId ??
        (!Array.isArray(entry?.items)
          ? entry?.items?.section?.id ??
            (typeof entry?.items?.section === "string"
              ? entry?.items?.section
              : undefined)
          : undefined) ??
        entry?.content?.section?.id ??
        (typeof entry?.content?.section === "string"
          ? entry.content?.section
          : undefined) ??
        entry?.content?.sectionID ??
        entry?.content?.sectionId ??
        entry?.payOrder?.box?.[0]?.section?.id ??
        (typeof entry?.payOrder?.box?.[0]?.section === "string"
          ? entry.payOrder?.box?.[0]?.section
          : undefined);

      const referal =
        entry?.referal ??
        entry?.referalID ??
        entry?.referalId ??
        (!Array.isArray(entry?.items) ? entry?.items?.referal : undefined) ??
        entry?.content?.referal ??
        entry?.content?.referalID ??
        entry?.content?.referalId ??
        entry?.payOrder?.box?.[0]?.referal;

      let data: any = {
        presentation,
        section,
        items: mappedItems,
        referal,
      };

      if (discountCode) {
        data.discountCode = discountCode;
      }

      if (entry?.availableTime || entry?.items?.availableTime) {
        data.availableTime =
          entry?.availableTime || entry?.items?.availableTime;
      }
      if (entry?.address || entry?.items?.address) {
        data.address = entry?.address || entry?.items?.address;
      } else {
        data.address = { addressLine1: null, city: null };
      }
      if (entry?.delivery || entry?.items?.delivery) {
        data.delivery = {
          isOpted: Boolean(
            entry?.delivery?.isOpted ||
              entry?.items?.delivery?.isOpted ||
              entry?.deliveryOpted
          ),
        };
      } else {
        data.delivery = { isOpted: false };
      }
      return data;
    });
  }
}
